import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAlgosStore } from "@/lib/stores/algosStore";
import { useUserStore } from "@/lib/stores/userStore";
import { Algo, AlgoType, AlgoStatus, AlgoOrder } from "./types";
import { SymbolSelect } from "@/components/shared/SymbolSelect";
import {
  Select as ShadcnSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  TradeAccount,
  useTradeAccountsStore,
} from "@/lib/stores/userTradeAccounts";
import { useDashboard } from "@/lib/store.ts";
import { useGrid } from "@/hooks/useGrid";
import { usePaneContext } from "@/components/pane/paneContext";
import { Slider } from "@/components/ui/slider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAccountInfoStore } from "@/lib/stores/accountInfoStore";
import { useBinanceAccount } from "@/hooks/exchanges/useBinanceAccount";
import axios from "axios";
import { useConfigsStore } from "../configs/configs.store";
import { environment } from "@/lib/constants";
import { PositionsList } from "./positions";

function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .substring(0, 32);
}

const ALGO_TYPES = [
  { value: "grid", label: "Grid" },
  { value: "chase", label: "Chase" },
  { value: "scale", label: "Scale" },
];

/**
 * Create a new deployment via the external API.
 * @param body The deployment payload
 */
async function createDeployment(body: any) {
  const url = `${environment.algoApiBase}/deploy`;
  return axios.post(url, body);
}

/**
 * Delete or stop a deployment via the external API.
 * @param id The deployment id (algo-${slug}-${userId.toLowerCase()}[:63])
 */
async function deleteDeployment(id: string) {
  const url = `${environment.algoApiBase}/deploy/${id}`;
  return axios.delete(url);
}

// Child component to call useBinanceAccount for each account
function BinanceAccountFetcher({ account }: { account: TradeAccount }) {
  useBinanceAccount(account);
  return null;
}

export function Algos() {
  const user = useUserStore((s) => s.user);
  const { algos, addAlgo, updateAlgo, removeAlgo, subscribeToAlgos } =
    useAlgosStore();
  const { accounts } = useTradeAccountsStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { configs, loadConfigs } = useConfigsStore();
  const [form, setForm] = useState<{
    name: string;
    coin: string;
    tradingAccountId: string;
    leverage: number;
    algoConfigId: string;
  }>({
    name: "",
    coin: "",
    tradingAccountId: "",
    leverage: 1,
    algoConfigId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedAlgoId = useAlgosStore((s) => s.selectedAlgoId);
  const setSelectedAlgoId = useAlgosStore((s) => s.setSelectedAlgoId);
  const selectedAlgo = algos.find((a) => a.id === selectedAlgoId) || null;
  const nodeId = usePaneContext();
  const { updateTabConfig, getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const color = config.color;

  const { colorsToSymbolMap, setSymbolForColor } = useDashboard((state) => ({
    colorsToSymbolMap: state.colorsToSymbolMap,
    setSymbolForColor: state.setSymbolForColor,
  }));

  const leverageSteps = [1, 5, 10, 25, 50, 75, 100];

  const [coinPopoverOpen, setCoinPopoverOpen] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      subscribeToAlgos(user.uid);
      loadConfigs(user.uid);
    }
    return () => {
      const unsubscribe = useAlgosStore.getState()._unsubscribeAlgos;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.uid, subscribeToAlgos, loadConfigs]);

  useEffect(() => {
    if (algos.length > 0 && !selectedAlgoId) {
      setSelectedAlgoId(algos[0].id);
    } else if (algos.length === 0) {
      setSelectedAlgoId(null);
    }
  }, [algos, selectedAlgoId, setSelectedAlgoId]);

  useEffect(() => {
    if (selectedAlgo) {
      setSymbolForColor(color, selectedAlgo.coin);
    }
  }, [selectedAlgo, setSymbolForColor, color]);

  const handleCreate = async () => {
    if (!form.coin || !form.tradingAccountId || !form.algoConfigId) {
      setError("Coin, trading account, and config are required");
      return;
    }
    if (!user?.uid) {
      setError("User not found");
      return;
    }
    setLoading(true);
    setError(null);
    const slug = slugify(form.name || `Algo ${Date.now()}`);
    const body = {
      coin: form.coin,
      leverage: Number(form.leverage.toFixed()),
      maxVolume: totalValue ? Number(totalValue.toFixed()) : 1000,
      name: form.name || `Algo ${Date.now()}`,
      slug,
      tradingAccountId: form.tradingAccountId,
      userId: user.uid,
      algoConfigId: form.algoConfigId,
    };
    const algoDocId = await addAlgo(body);
    try {
      await updateAlgo(algoDocId, { status: "Starting" as AlgoStatus });
      await createDeployment(body);
      setIsDialogOpen(false);
    } catch (e: any) {
      // 5. If POST fails, update status to 'Failed' in Firebase
      if (algoDocId) {
        await updateAlgo(algoDocId, { status: "Failed" as AlgoStatus });
      }
      setError(
        e.response?.data?.message || e.message || "Failed to create algo",
      );
    } finally {
      setLoading(false);
      setForm({
        name: "",
        coin: "",
        tradingAccountId: "",
        leverage: 1,
        algoConfigId: "",
      });
    }
  };

  const handleStatusChange = async (algo: Algo, status: AlgoStatus) => {
    setLoading(true);
    setError(null);
    if (!user?.uid) {
      setError("User not found");
      setLoading(false);
      return;
    }
    const deploymentId = `algo-${algo.slug}-${user.uid.toLowerCase()}`.slice(
      0,
      63,
    );
    try {
      if (status === "Stopping") {
        await deleteDeployment(deploymentId);
        await updateAlgo(algo.id, { status: "Stopped" });
      } else if (status === "Starting") {
        // Not supported by API, just update status in Firebase
        await updateAlgo(algo.id, { status: "Starting" });
      }
    } catch (e: any) {
      setError(
        e.response?.data?.message || e.message || "Failed to update status",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (algo: Algo) => {
    setLoading(true);
    setError(null);
    if (!user?.uid) {
      setError("User not found");
      setLoading(false);
      return;
    }
    const deploymentId = `algo-${algo.slug}-${user.uid.toLowerCase()}`.slice(
      0,
      63,
    );
    try {
      await deleteDeployment(deploymentId);
      await removeAlgo(algo.id);
    } catch (e: any) {
      // If backend returns 404 (not found), still remove from Firebase
      if (e.response?.status === 404) {
        await removeAlgo(algo.id);
      } else {
        setError(
          e.response?.data?.message || e.message || "Failed to delete algo",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const formatUSD = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const tradingAccountOptions = accounts.map((acc) => ({
    value: acc.id,
    label: acc.label || `${acc.exchange} (${acc.exchangeType})`,
  }));
  const { getAccountInfo } = useAccountInfoStore();
  const selectedAccount = accounts.find(
    (acc) => acc.id === form.tradingAccountId,
  );
  const accountInfo = selectedAccount?.id
    ? getAccountInfo(selectedAccount.id)
    : null;
  const balance = selectedAccount ? accountInfo?.availableBalance || "0" : null;
  const totalValue = balance ? parseFloat(balance) * form.leverage : null;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Algos</h2>
        <Button
          onClick={() => setIsDialogOpen(true)}
          variant="outline"
          size="sm"
        >
          + Create Algo
        </Button>
      </div>
      {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={false}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Algo</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Create a new algo to start trading.
          </DialogDescription>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreate();
            }}
            autoComplete="off"
          >
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="Algo name (optional)"
                className="w-full"
              />
            </div>
            <div className="space-y-2 w-full">
              <Label htmlFor="coin">Coin</Label>
              <Popover open={coinPopoverOpen} onOpenChange={setCoinPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {form.coin || "Select coin"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <SymbolSelect
                    value={form.coin}
                    onValueChange={(coin) => {
                      setForm((f) => ({ ...f, coin }));
                      setCoinPopoverOpen(false);
                    }}
                    className="w-full"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tradingAccountId">Trading Account</Label>
              <div className="w-full">
                <ShadcnSelect
                  value={form.tradingAccountId}
                  onValueChange={(value) =>
                    setForm((f) => ({ ...f, tradingAccountId: value }))
                  }
                  disabled={tradingAccountOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        tradingAccountOptions.length === 0
                          ? "No trading accounts found"
                          : "Select trading account"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {tradingAccountOptions.length === 0
                      ? null
                      : tradingAccountOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </ShadcnSelect>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="algoConfigId">Algo Config</Label>
              <ShadcnSelect
                value={form.algoConfigId}
                onValueChange={(value) =>
                  setForm((f) => ({ ...f, algoConfigId: value }))
                }
                disabled={configs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      configs.length === 0
                        ? "No configs found"
                        : "Select config"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {configs.length === 0
                    ? null
                    : configs.map((cfg) => (
                        <SelectItem key={cfg.id} value={cfg.id}>
                          {cfg.configName}
                        </SelectItem>
                      ))}
                </SelectContent>
              </ShadcnSelect>
            </div>
            <div className="space-y-2">
              <Label htmlFor="leverage">Leverage</Label>
              <ShadcnSelect
                value={form.leverage.toString()}
                onValueChange={(value) =>
                  setForm((f) => ({ ...f, leverage: Number(value) }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leverage" />
                </SelectTrigger>
                <SelectContent>
                  {leverageSteps.map((step) => (
                    <SelectItem key={step} value={step.toString()}>
                      {step}x
                    </SelectItem>
                  ))}
                </SelectContent>
              </ShadcnSelect>
            </div>
            <div className="space-y-2">
              <p>
                Total value:{" "}
                {balance && form.leverage
                  ? formatUSD(totalValue?.toString() || "0")
                  : "—"}
              </p>
            </div>
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <div className="mt-4">
        {loading ? (
          <div>Loading...</div>
        ) : algos.length === 0 ? (
          <div className="text-muted-foreground">No algos found.</div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-neutral-700">
                <th className="text-left py-1">Name</th>
                <th className="text-left py-1">Slug</th>
                <th className="text-left py-1">Coin</th>
                <th className="text-left py-1">Trading Account</th>
                <th className="text-left py-1">Leverage</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {algos.map((algo) => {
                const account = accounts.find(
                  (a) => a.id === algo.tradingAccountId,
                );
                const isSelected = selectedAlgo?.id === algo.id;
                return (
                  <tr
                    key={algo.id}
                    className={`border-b border-neutral-800 cursor-pointer transition-colors ${isSelected ? "bg-accent/5" : ""}`}
                    onClick={() => setSelectedAlgoId(algo.id)}
                  >
                    <td className="py-1">{algo.name}</td>
                    <td className="py-1">{algo.slug}</td>
                    <td className="py-1">{algo.coin}</td>
                    <td className="py-1">
                      {account ? (
                        account.label ||
                        `${account.exchange} (${account.exchangeType})`
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </td>
                    <td className="py-1">{algo.leverage}x</td>
                    <td className="py-1">{algo.status}</td>
                    <td className="py-1 flex gap-2">
                      {algo.status === "Created" ||
                      algo.status === "Stopped" ||
                      algo.status === "Failed" ? (
                        <Button
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(algo, "Starting");
                          }}
                          disabled={loading}
                        >
                          Start
                        </Button>
                      ) : null}
                      <Button
                        size="xs"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(algo);
                        }}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Preview warning */}
      <div className="mt-6 p-4 rounded-lg border border-yellow-600 bg-yellow-950 text-yellow-200 text-sm text-center">
        <strong>Preview:</strong> This feature is in preview state and may
        contain bugs. Use with caution and at your own risk.
      </div>
      {/* Details Section */}
      {algos.length === 0 ? (
        <div className="mt-6 p-4 rounded-lg border border-neutral-700 bg-neutral-900 text-muted-foreground">
          No algos created.
        </div>
      ) : selectedAlgo ? (
        <div className="mt-6 p-4 rounded-lg border border-neutral-700 bg-neutral-900">
          <h3 className="text-base font-semibold mb-2">
            Algo Details: {selectedAlgo.name}
          </h3>
          {selectedAlgo.status !== "Running" ? (
            <div className="text-muted-foreground">
              No details available. Algo is not started.
            </div>
          ) : (
            <>
              <div className="text-xs text-muted-foreground mb-2">Orders</div>
              {selectedAlgo.orders && selectedAlgo.orders.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left px-3 py-1 font-medium w-32">
                          OrderId
                        </th>
                        <th className="text-left px-3 py-1 font-medium w-16">
                          Side
                        </th>
                        <th className="text-left px-3 py-1 font-medium w-24">
                          Price
                        </th>
                        <th className="text-left px-3 py-1 font-medium w-16">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...selectedAlgo.orders]
                        .sort((a, b) => b.Price - a.Price)
                        .map((order: AlgoOrder) => (
                          <tr key={order.OrderID}>
                            <td className="text-left px-3 py-1 font-mono w-32">
                              {order.OrderID}
                            </td>
                            <td
                              className={`text-left px-3 py-1 w-16 ${order.Side === "BUY" ? "text-green-500" : "text-red-500"}`}
                            >
                              {order.Side}
                            </td>
                            <td className="text-left px-3 py-1 font-mono w-24">
                              {order.Price}
                            </td>
                            <td className="text-left px-3 py-1 font-mono w-16">
                              {order.Quantity}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted-foreground">
                  No order data available.
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
      {/* Fetch positions for all accounts */}
      {accounts.map((account) => (
        <BinanceAccountFetcher key={account.id} account={account} />
      ))}
      <div className="mt-6 p-4 rounded-lg border border-neutral-700 bg-neutral-900">
        <PositionsList symbol={config.symbol} />
      </div>
    </div>
  );
}
